import {
  ErrorModal,
  HelmetDexteel,
  useContextMenuMESF,
} from "@dexteel/mesf-core";
import { Button, Grid, Paper, Typography, Box } from "@mui/material";
import { Tree } from "@minoru/react-dnd-treeview";
import {
  DragLayerMonitorProps,
  DropOptions,
  NodeModel,
} from "@minoru/react-dnd-treeview/dist/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { CenteredLazyLoading } from "../../../../../controls/LazyLoading";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import type { CatalogDelayCode } from "../../models/CatalogDelayCode";
import { FileProperties } from "../../models/FileProperties";
import { TreeParameters } from "../../models/TreeParameter";
import type { DelayCodeLabels } from "../../delayCodeLabels";
import { CustomNode } from "../CustomNode";
import { DelayCodeSearchInput } from "../DelayCodeSearchInput";
import { CustomDragPreview } from "../DragPreview/CustomDragPreview";
import { MultipleDragPreview } from "../DragPreview/MultipleDragPreview";
import { useDelayActions } from "../hooks/delays/useDelayActions";
import { DelayCodeSearchResult } from "../hooks/delays/useDelayCodeSearch";
import { TransformDelayModelData } from "../hooks/TransformDelayModelData";
import { useDelayCodeOptionsFunctions } from "../hooks/useDelayCodeOptionsFunctions";
import { UpsertDeleteViewDelay } from "../modals/UpsertDeleteViewDelay";
import { useDelayCodesQuery } from "../../../hooks/queries/useDelayCodesQuery";
import { useMoveDelayCodeMutation } from "../../../hooks/mutations/useDelayCodeMutations";

interface TreeViewDelay {
  delayAreaAssetId?: number;
  delayAreaAssetName?: string;
  displayName?: string;
  labels: DelayCodeLabels;
}

export const TreeViewDelay = ({
  delayAreaAssetId,
  delayAreaAssetName,
  displayName,
  labels,
}: TreeViewDelay) => {
  const [selectedNodes, setSelectedNodes] = useState<
    NodeModel<FileProperties>[]
  >([]);
  const [showModal, setShowModal] = useState<
    "create" | "update" | "delete" | "view" | ""
  >("");

  const [isDragging, setIsDragging] = useState(false);
  const [isCtrlPressing, setIsCtrlPressing] = useState(false);

  const [delayCodeId, setDelayCodeId] = useState<number | null>();
  const [openDelays, setOpenDelays] = useState<
    Record<number | string, number | string>
  >({});
  const [delayNodeSelectedInTree, setDelayNodeSelectedInTree] =
    useState<CatalogDelayCode | null>(null);
  const [error, setError] = useState("");
  const [updatedSuccessfullyMessage, setUpdatedSuccessfullyMessage] =
    useState("");
  const [expandVersion, setExpandVersion] = useState(0);
  const hasSeededOpenDelays = useRef(false);

  const { data: allDelayCodes = [], isLoading: isLoadingCodes } =
    useDelayCodesQuery(delayAreaAssetId);

  const moveCodeMutation = useMoveDelayCodeMutation();

  const allDelayNodes = useMemo(() => {
    return allDelayCodes.map((row) => TransformDelayModelData(row));
  }, [allDelayCodes]);

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const sortBySortOrder = (
    a: NodeModel<FileProperties>,
    b: NodeModel<FileProperties>,
  ): number => {
    const nodeA = a as unknown as TreeParameters;
    const nodeB = b as unknown as TreeParameters;

    const sortA = nodeA.sortOrder;
    const sortB = nodeB.sortOrder;

    if (sortA === undefined || sortA === null) {
      if (sortB === undefined || sortB === null) {
        return 0;
      }
      return -1;
    }

    if (sortB === undefined || sortB === null) {
      return 1;
    }

    return sortA - sortB;
  };

  const { handleDrop, handleCtrlClick, handleDragStart, handleDragEnd } =
    useDelayActions({
      delay: allDelayNodes,
      selectedNodes,
      setSelectedNodes,
      setIsDragging,
      isCtrlPressing,
      setIsCtrlPressing,
      onDelayUpdateEnd: (result) => {
        if (result.ok) {
          setOpenDelays((prev) => ({
            ...prev,
            [result.dropTargetId]: result.dropTargetId,
          }));
          setUpdatedSuccessfullyMessage(
            `The ${labels.singular.toLowerCase()} was reordered successfully`,
          );
        } else {
          setError(result.message);
        }
      },
      labels,
    });

  const handleMoveUp = ({ delayCodeId }: { delayCodeId: number }) => {
    if (!delayCodeId) return;
    moveCodeMutation.mutate(
      { delayCodeId, moveDown: false },
      {
        onSuccess: () =>
          setUpdatedSuccessfullyMessage(
            `The ${labels.singular.toLowerCase()} was moved up successfully`,
          ),
        onError: (err) =>
          setError(
            err instanceof Error
              ? err.message
              : `Error moving ${labels.singular.toLowerCase()}`,
          ),
      },
    );
  };

  const handleMoveDown = ({ delayCodeId }: { delayCodeId: number }) => {
    if (!delayCodeId) return;
    moveCodeMutation.mutate(
      { delayCodeId, moveDown: true },
      {
        onSuccess: () =>
          setUpdatedSuccessfullyMessage(
            `The ${labels.singular.toLowerCase()} was moved down successfully`,
          ),
        onError: (err) =>
          setError(
            err instanceof Error
              ? err.message
              : `Error moving ${labels.singular.toLowerCase()}`,
          ),
      },
    );
  };

  const { getMenuOptions } = useDelayCodeOptionsFunctions({
    setDelayCodeId,
    setShowModal,
    handleMoveUp,
    handleMoveDown,
    labels,
  });

  const handleContextMenu = (
    data: NodeModel<FileProperties>,
    e: React.MouseEvent<HTMLElement>,
  ) => {
    e.preventDefault();
    if (e.type === "contextmenu") {
      setSelectedNodes([data]);
      showContextMenu(e, data, "delayCodeContext");
      const selectedNode = allDelayCodes.find(
        (node) => node.DelayCodeId === data.id,
      );
      if (selectedNode) {
        setDelayNodeSelectedInTree(selectedNode);
      }
    }
  };

  const onNodeDoubleClicked = (
    data: NodeModel<FileProperties>,
    e: React.MouseEvent<HTMLElement>,
  ) => {
    e.preventDefault();
    setDelayCodeId((data as TreeParameters).id);
    setShowModal("view");
  };

  const handleSearchResultClick = (result: DelayCodeSearchResult) => {
    const nodeMap = new Map<number, NodeModel<FileProperties>>();
    allDelayNodes.forEach((node) => {
      nodeMap.set(node.id as number, node);
    });

    const parentsToExpand: { [key: number | string]: number | string } = {
      ...openDelays,
    };
    let currentNode = nodeMap.get(result.id);

    while (currentNode && currentNode.parent !== 0) {
      const parentId = currentNode.parent as number;
      parentsToExpand[parentId] = parentId;
      currentNode = nodeMap.get(parentId);
    }

    setOpenDelays(parentsToExpand);
    setExpandVersion((version) => version + 1);

    const nodeToSelect = nodeMap.get(result.id);
    if (nodeToSelect) {
      setSelectedNodes([nodeToSelect]);

      const selectedDelayCode = allDelayCodes.find(
        (code: CatalogDelayCode) => code.DelayCodeId === result.id,
      );
      if (selectedDelayCode) {
        setDelayNodeSelectedInTree(selectedDelayCode);
      }

      setTimeout(() => {
        const nodeElement = document.querySelector(
          `[data-node-id="${result.id}"]`,
        );
        if (nodeElement) {
          nodeElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
    }
  };

  const onModalHide = () => {
    setShowModal("");
    setDelayNodeSelectedInTree(null);
  };

  const openDelaysArray = useMemo(() => {
    return Object.keys(openDelays).map((el) => parseInt(el));
  }, [openDelays]);

  useEffect(() => {
    if (hasSeededOpenDelays.current || allDelayNodes.length === 0) return;
    hasSeededOpenDelays.current = true;
    setOpenDelays(
      allDelayNodes
        .filter((node) => node.parent === 0)
        .reduce(
          (acc, node) => ({ ...acc, [node.id]: node.id }),
          {} as Record<number | string, number | string>,
        ),
    );
  }, [allDelayNodes]);

  useEffect(() => {
    registerConfig({
      id: "delayCodeContext",
      getOptions: getMenuOptions,
    });
  }, []);

  if (isLoadingCodes) return <CenteredLazyLoading />;

  return (
    <>
      <HelmetDexteel
        title={
          displayName ??
          (delayAreaAssetName
            ? `${delayAreaAssetName} ${labels.plural}`
            : labels.plural)
        }
      />
      <Box sx={{ width: "100%", height: "100%" }}>
        <style>
          {`
            .tree-asset-root {
              box-sizing: border-box;
              height: 100%;
              margin: 0;
              padding: 6px 0 0 6px;
              list-style-type: none;
            }
            .tree-asset-root ul,
            .tree-asset-root ol {
              list-style-type: none;
              margin: 0;
              padding: 0;
            }
            .tree-drop-target {
              box-shadow: 0 0 0 1px #1967d2 inset;
            }
          `}
        </style>
        <Box
          onContextMenu={(e) => showContextMenu(e, null, "delayCodeContext")}
        >
          <Box>
            <Grid container justifyContent="center" alignItems="center">
              <Box
                sx={{
                  width: "100%",
                  height: "calc(100vh - 112px)",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  padding: "10px 15px 20px",
                }}
              >
                <Typography
                  variant="h5"
                  style={{ margin: "0 0 10px", fontWeight: 600 }}
                >
                  {displayName ?? (
                    <>
                      {labels.plural} Tree{" "}
                      {delayAreaAssetName ? `- ${delayAreaAssetName}` : ""}
                    </>
                  )}
                </Typography>
                <DelayCodeSearchInput
                  delayNodes={allDelayNodes}
                  onSearchResultClick={handleSearchResultClick}
                  labels={labels}
                />
                <Paper
                  elevation={2}
                  style={{
                    width: "100%",
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    padding: 0,
                    borderRadius: "0.75rem",
                    marginBottom: 15,
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      width: "100%",
                      overflowY: "auto",
                      paddingBottom: 10,
                    }}
                  >
                    <Tree
                      key={`${JSON.stringify(allDelayNodes)}-${expandVersion}`}
                      rootId={0}
                      initialOpen={openDelaysArray}
                      tree={allDelayNodes}
                      sort={sortBySortOrder}
                      insertDroppableFirst={false}
                      enableAnimateExpand={true}
                      classes={{
                        root: "tree-asset-root",
                        dropTarget: "tree-drop-target",
                      }}
                      onDrop={handleDrop}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      canDrop={(
                        dcode: NodeModel<FileProperties>[],
                        options: DropOptions<FileProperties>,
                      ): boolean => {
                        if (options.dropTargetId === 0) return false;
                        return !selectedNodes.some(
                          (selectedNode) =>
                            selectedNode.id === options.dropTargetId,
                        );
                      }}
                      render={(node, options) => {
                        const selected = selectedNodes.some(
                          (selectedNode) => selectedNode.id === node.id,
                        );
                        return (
                          <CustomNode
                            setContextMenuOver={handleContextMenu}
                            node={node}
                            {...options}
                            isSelected={selected}
                            isDragging={selected && isDragging}
                            onClick={handleCtrlClick}
                            onDoubleClick={onNodeDoubleClicked}
                            openDelays={openDelays}
                            setOpenDelays={setOpenDelays}
                          />
                        );
                      }}
                      dragPreviewRender={(
                        monitorProps: DragLayerMonitorProps<FileProperties>,
                      ) => {
                        if (selectedNodes.length > 1) {
                          return (
                            <MultipleDragPreview dragSources={selectedNodes} />
                          );
                        }
                        return (
                          <CustomDragPreview monitorProps={monitorProps} />
                        );
                      }}
                    />
                  </div>
                </Paper>
                <Grid container justifyContent="flex-end">
                  <Grid
                    item
                    md={3}
                    sm={4}
                    xs={12}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "20px",
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      style={{ margin: "0px 15px" }}
                      onClick={() => {
                        setDelayNodeSelectedInTree(null);
                        setShowModal("create");
                      }}
                    >
                      {`NEW ${labels.singular.toUpperCase()}`}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Box>
        </Box>
      </Box>
      <UpsertDeleteViewDelay
        DelayId={delayCodeId}
        modal={showModal}
        onHide={onModalHide}
        delayAreaAssetId={delayAreaAssetId}
        delayAreaAssetName={delayAreaAssetName}
        delayNodeSelectedInTree={delayNodeSelectedInTree}
        allDelayCodes={allDelayCodes}
        labels={labels}
      />
      <NotificationSnackBar
        message={updatedSuccessfullyMessage}
        onHide={() => setUpdatedSuccessfullyMessage("")}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
